import axios from "axios";
import { useContext, useState } from "react";
import {
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import toast from "react-hot-toast";
import { AppContext } from "../../scripts/context/App";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 900,
  minHeight: "40vh",
  bgcolor: "#fff",
  borderRadius: "4px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

interface IProps {
  data: any;
  setData: (data: any) => void;
  handleClose: () => void;
  getCategories: () => void;
}
export default function CategoryModal({
  data,
  setData,
  handleClose,
  getCategories,
}: IProps) {
  const { user } = useContext(AppContext);
  const [isCreating, setIsCreating] = useState(false);
  const handleChange = (e: any) => {
    setData({ [e.target.name]: e.target.value });
  };

  const submitModal = async () => {
    if (!user.permissions.includes("add_support_category"))
      return toast.error("You don't have permission to do this");
    if (!data.data.name.trim() || !data.data.notes.trim())
      return toast.error("Please fill all the fields");

    try {
      setIsCreating(true);
      await axios.post("/support-categories/create", {
        name: data.data.name,
        notes: data.data.notes,
      });

      toast.success("Category created successfully");
      getCategories();
      handleClose();
    } catch (error: any) {
      console.log(error);

      toast.error(
        error.response.data.message || "Something went wrong, please try again"
      );
    }
    setIsCreating(false);
  };

  const editCategory = async () => {
    if (!user.permissions.includes("edit_support_category"))
      return toast.error("You don't have permission to do this");
    if (!data.data.name || !data.data.notes)
      return toast.error("Please fill all fields");

    try {
      setIsCreating(true);
      await axios.post("/support-categories/update", {
        category_id: data.data.id,
        name: data.data.name,
        notes: data.data.notes,
      });
      toast.success("Category updated successfully");
      setIsCreating(false);
      getCategories();
      handleClose();
    } catch (error: any) {
      toast.error(
        "Something went wrong. Please try again later or contact support."
      );
      console.log(error);

      setIsCreating(false);
    }
  };

  return (
    <Modal
      keepMounted
      open={data.status}
      onClose={handleClose}
      aria-labelledby="Category-Modal"
    >
      <Box sx={style}>
        <Box sx={{ p: 3, borderBottom: "1px solid #d3d3d3" }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{ wordBreak: "break-all" }}
          >
            {data.edit ? `Edit ${data.data.name} Category` : "Add New Category"}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            overflowY: "auto",
            maxHeight: "60vh",
          }}
        >
          <TextField
            id={`filled-category-name`}
            name="name"
            label="Category Name"
            variant="standard"
            value={data.data.name}
            onChange={handleChange}
            sx={{ flex: "1 1 40%" }}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            id={`filled-category-notes`}
            name="notes"
            label="Category Note"
            variant="standard"
            value={data.data.notes}
            onChange={handleChange}
            sx={{ flex: "1 1 40%" }}
            inputProps={{ maxLength: 2000 }}
          />
        </Box>
        <Box
          sx={{
            borderTop: "1px solid #d3d3d3",
            p: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={data.edit ? editCategory : submitModal}
            disabled={isCreating}
            endIcon={isCreating && <CircularProgress size={20} />}
          >
            {data.edit ? "Edit" : "Create"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={isCreating}
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
